// src/components/common/Navbar.jsx
// Desktop: cart icon button top-right → opens CartDrawer
// Mobile: cart goes to /cart page via BottomNav
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguagesContext";
import { useCart } from "../../context/CartContext";
import { logout } from "../../services/authService";
import CartDrawer from "./CartDrawer";
import logoImg from "../../assets/phsar_game.PNG";

const IconCartBtn = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);

export default function Navbar() {
  const { user, isAdmin }       = useAuth();
  const { lang, toggleLang, t } = useLang();
  const { cartCount }           = useCart();
  const navigate                = useNavigate();
  const location                = useLocation();

  const [scrolled,      setScrolled]      = useState(false);
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [dropdownOpen,  setDropdownOpen]  = useState(false);
  const [cartOpen,      setCartOpen]      = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  async function handleLogout() { await logout(); navigate("/"); }
  const isActive = (p) => location.pathname === p;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          height: 60px;
          background: ${scrolled ? "rgba(18,18,18,0.97)" : "rgba(18,18,18,0.85)"};
          backdrop-filter: blur(20px);
          border-bottom: 1px solid ${scrolled ? "rgba(255,255,255,0.08)" : "transparent"};
          transition: all 0.2s;
        }
        .nav-inner {
          max-width: 1400px; margin: 0 auto; height: 100%;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 24px; gap: 32px;
        }

        /* Logo */
        .nav-logo {
          display: flex; align-items: center; gap: 6px;
          font-family: 'Inter', sans-serif; font-size: 17px; font-weight: 800;
          color: #fff; text-decoration: none; flex-shrink: 0; letter-spacing: -0.3px;
        }
        .nav-logo img { width: 28px; height: 28px; object-fit: contain; }
        .nav-logo-accent { color: #0078F2; }

        /* Links */
        .nav-links { display: flex; align-items: center; gap: 2px; flex: 1; }
        .nav-link {
          font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 500;
          color: #a3a3a3; text-decoration: none;
          padding: 6px 12px; border-radius: 6px; transition: all 0.15s;
          position: relative;
        }
        .nav-link:hover { color: #fff; background: rgba(255,255,255,0.06); }
        .nav-link.active { color: #fff; font-weight: 600; }
        .nav-link.active::after {
          content: '';
          position: absolute; bottom: -2px; left: 50%; transform: translateX(-50%);
          width: 18px; height: 2px; border-radius: 2px;
          background: #0078F2;
        }

        /* Right side */
        .nav-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

        /* Language toggle */
        .lang-pill {
          display: flex; background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; overflow: hidden;
        }
        .lang-btn {
          font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600;
          padding: 5px 10px; background: none; border: none; cursor: pointer;
          color: #6b6b6b; transition: all 0.15s;
        }
        .lang-btn.active { background: #0078F2; color: #fff; }

        /* ── CART BUTTON ── */
        .nav-cart-btn {
          position: relative;
          display: flex; align-items: center; justify-content: center;
          width: 38px; height: 38px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px; cursor: pointer; color: #a3a3a3;
          transition: all 0.15s;
        }
        .nav-cart-btn:hover {
          background: rgba(0,120,242,0.12);
          border-color: rgba(0,120,242,0.3);
          color: #fff;
        }
        .nav-cart-btn.has-items {
          border-color: rgba(0,120,242,0.4);
          color: #0078F2;
        }
        /* Badge */
        .nav-cart-badge {
          position: absolute; top: -6px; right: -6px;
          background: #0078F2; color: #fff;
          font-family: 'Inter', sans-serif; font-size: 9px; font-weight: 800;
          min-width: 16px; height: 16px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          padding: 0 4px; line-height: 1;
          border: 2px solid #121212;
          animation: badgePop 0.25s cubic-bezier(0.34,1.56,0.64,1) both;
        }
        @keyframes badgePop {
          from { transform: scale(0); opacity: 0; }
          to   { transform: scale(1); opacity: 1; }
        }

        /* Auth buttons */
        .btn-login {
          font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600;
          color: #a3a3a3; background: none; border: none; padding: 8px 14px;
          border-radius: 6px; cursor: pointer; text-decoration: none; transition: all 0.15s;
        }
        .btn-login:hover { color: #fff; background: rgba(255,255,255,0.06); }
        .btn-getstarted {
          font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 700;
          color: #fff; background: #0078F2; border: none;
          padding: 9px 20px; border-radius: 6px; cursor: pointer;
          text-decoration: none; transition: all 0.15s;
        }
        .btn-getstarted:hover { background: #0a84ff; }

        /* Avatar dropdown */
        .user-menu { position: relative; }
        .user-avatar {
          width: 34px; height: 34px; background: #0078F2; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700;
          color: #fff; cursor: pointer; transition: all 0.15s;
        }
        .user-avatar:hover { background: #0a84ff; }
        .user-dropdown {
          position: absolute; top: calc(100% + 8px); right: 0;
          background: #1a1a1a; border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px; padding: 6px; min-width: 200px;
          box-shadow: 0 16px 48px rgba(0,0,0,0.6);
          animation: dropIn 0.15s ease; z-index: 200;
        }
        @keyframes dropIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        .dd-header { padding: 10px 12px; border-bottom: 1px solid rgba(255,255,255,0.06); margin-bottom: 4px; }
        .dd-name { font-size: 14px; font-weight: 600; color: #fff; margin-bottom: 2px; }
        .dd-email { font-size: 11px; color: #6b6b6b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .dd-item {
          display: flex; align-items: center; gap: 9px;
          padding: 9px 12px; border-radius: 6px;
          font-size: 13px; font-weight: 500; color: #a3a3a3;
          text-decoration: none; cursor: pointer; transition: all 0.12s;
          background: none; border: none; width: 100%; text-align: left;
        }
        .dd-item:hover { background: rgba(255,255,255,0.06); color: #fff; }
        .dd-item.danger:hover { background: rgba(239,68,68,0.1); color: #ef4444; }
        .dd-item.admin { color: #0078F2; }
        .dd-item.admin:hover { background: rgba(0,120,242,0.1); color: #0a84ff; }
        .dd-divider { height: 1px; background: rgba(255,255,255,0.06); margin: 4px 0; }

        /* Hamburger — tablet only */
        .hamburger {
          display: none; background: none; border: none;
          color: #a3a3a3; font-size: 20px; padding: 4px; cursor: pointer;
        }
        .hamburger:hover { color: #fff; }

        /* Tablet slide-down */
        .mobile-nav {
          display: none; position: fixed; top: 60px; left: 0; right: 0;
          background: rgba(18,18,18,0.98); backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.08);
          padding: 12px 20px 20px; z-index: 999;
          animation: slideDown 0.18s ease;
        }
        @keyframes slideDown { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
        .mobile-link {
          display: block; font-size: 15px; font-weight: 500;
          color: #a3a3a3; text-decoration: none;
          padding: 12px 0; border-bottom: 1px solid rgba(255,255,255,0.05);
          transition: color 0.15s;
        }
        .mobile-link:hover, .mobile-link.active { color: #fff; }
        .mobile-actions { display: flex; gap: 8px; margin-top: 14px; flex-wrap: wrap; }
        .mobile-lang {
          display: flex; align-items: center; gap: 8px; margin-top: 14px;
          padding-top: 14px; border-top: 1px solid rgba(255,255,255,0.05);
        }
        .mobile-lang-label { font-size: 12px; color: #6b6b6b; }

        /* Tablet (768–1100px) */
        @media (min-width: 768px) and (max-width: 1100px) {
          .nav-links { display: none; }
          .hamburger { display: block; }
          .mobile-nav.open { display: block; }
          .btn-login, .btn-getstarted { display: none; }
        }

        /* Mobile (≤767px) — minimal bar, BottomNav handles navigation */
        @media (max-width: 767px) {
          .nav-links { display: none; }
          .hamburger { display: none; }
          .mobile-nav { display: none !important; }
          .btn-login, .btn-getstarted { display: none; }
          /* Hide cart button on mobile — BottomNav has it */
          .nav-cart-btn { display: none; }
          .nav-inner { padding: 0 16px; gap: 12px; }
          .nav-logo { font-size: 15px; }
          .nav-logo img { width: 24px; height: 24px; }
        }
      `}</style>

      <nav className="nav">
        <div className="nav-inner">
          {/* Logo */}
          <Link to="/" className="nav-logo">
            <img src={logoImg} alt="PhsarGame" />
            Phsar<span className="nav-logo-accent">Game</span>
          </Link>

          {/* Desktop links */}
          <div className="nav-links">
            <Link to="/" className={`nav-link ${isActive("/") ? "active" : ""}`}>{t("nav_home")}</Link>
            <Link to="/store" className={`nav-link ${isActive("/store") ? "active" : ""}`}>{t("nav_store")}</Link>
            <Link to="/how-to-use" className={`nav-link ${isActive("/how-to-use") ? "active" : ""}`}>{t("nav_how")}</Link>
            {user && <Link to="/orders" className={`nav-link ${isActive("/orders") ? "active" : ""}`}>{t("nav_orders")}</Link>}
          </div>

          {/* Right controls */}
          <div className="nav-right">
            {/* Language */}
            <div className="lang-pill">
              <button className={`lang-btn ${lang === "en" ? "active" : ""}`} onClick={() => lang !== "en" && toggleLang()}>EN</button>
              <button className={`lang-btn ${lang === "km" ? "active" : ""}`} onClick={() => lang !== "km" && toggleLang()}>ខ្មែរ</button>
            </div>

            {/* ── CART BUTTON (desktop only) ── */}
            <button
              className={`nav-cart-btn ${cartCount > 0 ? "has-items" : ""}`}
              onClick={() => setCartOpen(true)}
              aria-label={`Cart (${cartCount} items)`}
            >
              <IconCartBtn />
              {cartCount > 0 && (
                <span key={cartCount} className="nav-cart-badge">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </button>

            {/* User avatar / auth buttons */}
            {user ? (
              <div className="user-menu">
                <div className="user-avatar" onClick={() => setDropdownOpen(!dropdownOpen)}>
                  {(user.displayName || user.email || "U")[0].toUpperCase()}
                </div>
                {dropdownOpen && (
                  <>
                    <div style={{ position:"fixed", inset:0, zIndex:-1 }} onClick={() => setDropdownOpen(false)} />
                    <div className="user-dropdown">
                      <div className="dd-header">
                        <div className="dd-name">{user.displayName || "User"}</div>
                        <div className="dd-email">{user.email}</div>
                      </div>
                      <Link to="/profile" className="dd-item">👤 Profile</Link>
                      <Link to="/orders" className="dd-item">🎮 {t("nav_orders")}</Link>
                      {isAdmin && (
                        <>
                          <div className="dd-divider" />
                          <Link to="/admin" className="dd-item admin">⚡ {t("nav_admin")}</Link>
                        </>
                      )}
                      <div className="dd-divider" />
                      <button onClick={handleLogout} className="dd-item danger">🚪 {t("nav_signout")}</button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn-login">{t("nav_login")}</Link>
                <Link to="/register" className="btn-getstarted">{t("nav_started")}</Link>
              </>
            )}

            {/* Tablet hamburger */}
            <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>
      </nav>

      {/* Tablet slide-down menu */}
      <div className={`mobile-nav ${menuOpen ? "open" : ""}`}>
        <Link to="/" className={`mobile-link ${isActive("/") ? "active" : ""}`}>{t("nav_home")}</Link>
        <Link to="/store" className={`mobile-link ${isActive("/store") ? "active" : ""}`}>{t("nav_store")}</Link>
        <Link to="/how-to-use" className={`mobile-link ${isActive("/how-to-use") ? "active" : ""}`}>{t("nav_how")}</Link>
        {user && <Link to="/orders" className={`mobile-link ${isActive("/orders") ? "active" : ""}`}>{t("nav_orders")}</Link>}
        {isAdmin && <Link to="/admin" className="mobile-link" style={{ color:"#0078F2" }}>⚡ {t("nav_admin")}</Link>}
        <div className="mobile-actions">
          {user ? (
            <button onClick={handleLogout} className="btn-login" style={{ cursor:"pointer" }}>{t("nav_signout")}</button>
          ) : (
            <>
              <Link to="/login" className="btn-login">{t("nav_login")}</Link>
              <Link to="/register" className="btn-getstarted">{t("nav_started")}</Link>
            </>
          )}
        </div>
        <div className="mobile-lang">
          <span className="mobile-lang-label">Language:</span>
          <div className="lang-pill">
            <button className={`lang-btn ${lang === "en" ? "active" : ""}`} onClick={() => lang !== "en" && toggleLang()}>EN</button>
            <button className={`lang-btn ${lang === "km" ? "active" : ""}`} onClick={() => lang !== "km" && toggleLang()}>ខ្មែរ</button>
          </div>
        </div>
      </div>

      {/* Cart Drawer — desktop only (hidden via CSS on mobile) */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
}